import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { PermissionChecklist } from './PermissionChecklist';
import { usePermissionCatalog, useReplaceUserPermissions } from './useUsers';
import type { EfsUser } from './types';

const NS = 'settings';

interface UserPermissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: EfsUser;
}

/**
 * Ticks the account's permission set directly (vendors never see roles).
 * Saving replaces the whole set via `PUT /users/:id/permissions`.
 */
export const UserPermissionsDialog = ({ open, onOpenChange, user }: UserPermissionsDialogProps) => {
  const { t } = useTranslation(NS);
  const isSystemAdmin = user.roles.includes('system_admin');
  // Companyless accounts have no party catalog; the backend refuses the save.
  const hasNoCompany = !user.company_id;
  const editable = !isSystemAdmin && !hasNoCompany;
  const replace = useReplaceUserPermissions();
  // The catalog is scoped to the target user's company party. Company admins
  // may pass their own company id explicitly — the BE accepts it.
  const catalog = usePermissionCatalog(user.company_id ?? undefined, {
    enabled: open && editable,
  });
  const [draft, setDraft] = useState<string[]>(user.permissions);

  useEffect(() => {
    if (open) setDraft(user.permissions);
  }, [open, user]);

  const save = async () => {
    await replace.mutateAsync({ id: user.user_id, permission_codes: draft });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('efs.settings.users.dialog.roles_title')}</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>

        {isSystemAdmin ? (
          <p className="rounded-md bg-efs-bg-subtle px-3 py-2 text-sm text-efs-text-muted">
            {t('efs.settings.users.messages.system_admin_permissions_locked')}
          </p>
        ) : hasNoCompany ? (
          <p className="rounded-md bg-efs-bg-subtle px-3 py-2 text-sm text-efs-text-muted">
            {t('efs.settings.users.messages.no_company_permissions')}
          </p>
        ) : catalog.isLoading ? (
          <p className="py-6 text-center text-sm text-efs-text-muted">
            {t('efs.common.messages.loading', { ns: 'common' })}
          </p>
        ) : (
          <PermissionChecklist catalog={catalog.data ?? []} value={draft} onChange={setDraft} />
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={replace.isPending}
          >
            {t('efs.common.actions.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => void save()}
            disabled={!editable || replace.isPending}
          >
            {replace.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('efs.common.actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
